import React, { FC, useCallback, useEffect, useState } from 'react';
import Button from '@atlaskit/button';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import CheckIcon from '@atlaskit/icon/glyph/check';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import WorldIcon from '@atlaskit/icon/glyph/world';
import { token } from '@atlaskit/tokens';
import {
  selectCurrentLanguage,
  selectProposedLanguage,
  selectSupportedLanguages,
  switchLanguage,
  translationKeys,
  useTranslation,
} from 'services/localization';
import { useAppDispatch, useAppSelector } from '../../../../hooks';
import { Box, xcss } from '@atlaskit/primitives';
import { css } from '@compiled/react';
import { Hide } from '@atlaskit/primitives/responsive';

const languageBarWrapperStyles = xcss({
  position: 'fixed',
  top: token('space.800'),
  left: '0',
  width: '100%',
  textAlign: 'center',
  zIndex: 'navigation',
});

const barContentStyles = css({
  display: 'flex',
  justifyContent: 'space-around',
  alignItems: 'center',
  width: '510px',
  margin: `${token('space.050')} auto`,
  padding: token('space.100'),
  backgroundColor: token('color.icon.inverse'),
  borderRadius: '3px',
  boxShadow: `0 2px 6px 0 ${token('color.background.accent.gray.subtler')}`,
});

const contentDelimiterStyles = css({
  width: '1px',
  height: '20px',
  '&::after': {
    content: "''",
    display: 'block',
    width: '1px',
    height: '20px',
    backgroundColor: token('color.icon.inverse'),
  },
});

const closeButtonStyles = css({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '3px',
  backgroundColor: token('color.background.neutral.subtle'),
  '&:hover': {
    backgroundColor: token('color.background.neutral.subtle.hovered'),
  },
});

const LanguageBar: FC = () => {
  const [show, setShow] = useState(true);
  const { translate } = useTranslation();

  const proposedLanguage = useAppSelector(selectProposedLanguage);
  const currentLanguage = useAppSelector(selectCurrentLanguage);
  const supportedLanguages = useAppSelector(selectSupportedLanguages);

  const dispatch = useAppDispatch();
  const selectLanguageHandler = useCallback(
    (language: string) => () => {
      dispatch(switchLanguage(language));
      setShow(false);
    },
    [dispatch],
  );

  useEffect(() => {
    if (proposedLanguage == null) setShow(true);
  }, [proposedLanguage]);

  if (proposedLanguage == null || !show) return null;

  return (
    <Hide below='sm'>
      <Box as='div' testId='language-bar-wrapper' xcss={languageBarWrapperStyles}>
        <div css={barContentStyles}>
          <WorldIcon
            label={'header-world-icon'}
            primaryColor={token('color.icon.accent.gray')}
            secondaryColor={token('color.icon.accent.gray')}
            size={'large'}
          />
          <Button
            appearance={'link'}
            onClick={selectLanguageHandler(proposedLanguage)}
            testId='proposed-language-bar-button'
          >
            {translate(translationKeys.PROPOSED_LANGUAGE_MESSAGE, { language: supportedLanguages[proposedLanguage] })}
          </Button>
          <div css={contentDelimiterStyles} />
          <div>
            <DropdownMenu testId='language-bar-dropdown' trigger={translate(translationKeys.ALL_LANGUAGES_MESSAGE)}>
              <DropdownItemGroup>
                {Object.keys(supportedLanguages).map((language) => (
                  <DropdownItem
                    key={language}
                    elemBefore={
                      language === currentLanguage ? <CheckIcon label={'check-icon'} size={'small'} /> : undefined
                    }
                    onClick={selectLanguageHandler(language)}
                    title={`language-bar-dropdown-item-${language}`}
                  >
                    {supportedLanguages[language]}
                  </DropdownItem>
                ))}
              </DropdownItemGroup>
            </DropdownMenu>
          </div>
          <div onClick={() => setShow(false)} data-testid='language-bar-close-button' css={closeButtonStyles}>
            <CrossIcon
              size='medium'
              label={'message-close-icon'}
              primaryColor={token('color.icon.accent.gray')}
              secondaryColor={token('color.icon.accent.gray')}
            />
          </div>
        </div>
      </Box>
    </Hide>
  );
};

export default LanguageBar;
